import React from 'react';
import { XMarkIcon, SparklesIcon, ExclamationTriangleIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

export const CONTACTO_LABEL = {
    titular: 'Titular',
    tercero: 'Tercero',
    numero_equivocado: 'Número equivocado',
    buzon: 'Buzón de voz',
    sin_contacto: 'Sin contacto',
};

/** Color del ánimo del cliente según el sentimiento (positivo / neutro / negativo). */
export const colorSentimiento = (texto) => {
    const t = (texto || '').toLowerCase();
    if (t.includes('negativo')) return 'bg-red-100 text-red-700 dark:bg-rose-950/60 dark:text-rose-300';
    if (t.includes('positivo')) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300';
    return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
};

export const formatDuracion = (segundos) => {
    if (segundos === null || segundos === undefined) return '';
    const s = Number(segundos) || 0;
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const legible = (texto) => (texto || '').replace(/_/g, ' ');

/** 'Operador: Hola. Cliente: Aló. …' → [{ quien: 'Operador', texto: 'Hola.' }, …] */
const separarTurnos = (transcript) => {
    const partes = (transcript || '').split(/(Operador|Cliente):\s*/);
    const turnos = [];
    for (let i = 1; i < partes.length; i += 2) {
        const texto = (partes[i + 1] || '').trim();
        if (texto) turnos.push({ quien: partes[i], texto });
    }
    return turnos;
};

const formatFecha = (fecha) => {
    if (!fecha) return '';
    const [y, m, d] = String(fecha).split('T')[0].split('-');
    return y && m && d ? `${parseInt(d, 10)}/${parseInt(m, 10)}/${y}` : '';
};

function Dato({ label, children }) {
    return (
        <div>
            <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">{label}</p>
            <div className="text-sm text-slate-800 dark:text-slate-200 font-medium">{children || '-'}</div>
        </div>
    );
}

/**
 * Detalle de una gestión registrada automáticamente desde la llamada (Origen AUTO_CDR):
 * audio, resumen, ánimo del cliente, promesa de pago y la conversación Operador / Cliente.
 */
export function ModalConversacion({ gestion, onClose }) {
    if (!gestion) return null;
    const turnos = separarTurnos(gestion.Transcript);
    const animo = gestion.SentimientoPersona
        || [gestion.Actitud, gestion.Sentimiento].filter(Boolean).join(' / ');

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white dark:bg-[#111827] dark:border dark:border-slate-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="bg-gradient-to-r from-slate-800 to-slate-900 dark:from-[#0D131F] dark:to-[#0D131F] dark:border-b dark:border-slate-800 px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-1.5 bg-violet-500 rounded-lg">
                            <SparklesIcon className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-white tracking-wide">GESTIÓN AUTOMÁTICA DE LA LLAMADA</h3>
                            <p className="text-blue-200 text-[11px]">
                                {gestion.Fecha ? new Date(gestion.Fecha).toLocaleString('es-EC') : ''} · {gestion.Operador_Cobrador || gestion.Usuario} · {gestion.Telefono}
                            </p>
                        </div>
                    </div>
                    <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10" title="Cerrar">
                        <XMarkIcon className="w-5 h-5 text-white" />
                    </button>
                </div>

                <div className="overflow-y-auto p-5 space-y-4">
                    {gestion.UrlGrabacion && (
                        <div>
                            <audio controls preload="none" src={gestion.UrlGrabacion} className="w-full">
                                Tu navegador no puede reproducir el audio.
                            </audio>
                            <a href={gestion.UrlGrabacion} target="_blank" rel="noopener noreferrer" download
                                className="inline-flex items-center gap-1 mt-1 text-xs font-semibold text-blue-600 dark:text-indigo-400 hover:underline">
                                <ArrowDownTrayIcon className="w-3.5 h-3.5" /> Descargar grabación
                            </a>
                        </div>
                    )}

                    <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-[11px] text-amber-800 dark:bg-amber-950/40 dark:border-amber-800/50 dark:text-amber-200/90">
                        <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        Generado automáticamente a partir del audio. Puede contener errores: escucha la grabación antes de tomar decisiones.
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200 dark:bg-[#161F30] dark:border-slate-800">
                        <Dato label="Resultado">{gestion.Resultado}</Dato>
                        <Dato label="Contestó">{CONTACTO_LABEL[gestion.Contacto] || legible(gestion.Contacto)}</Dato>
                        <Dato label="Ánimo del cliente">
                            {animo ? <span className={`px-2 py-0.5 rounded text-xs font-bold ${colorSentimiento(animo)}`}>{legible(animo)}</span> : null}
                        </Dato>
                        <Dato label="Duración">{formatDuracion(gestion.DuracionLlamada)}</Dato>
                    </div>

                    {gestion.PromesaPago && (
                        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl dark:bg-emerald-950/40 dark:border-emerald-800/50">
                            <p className="text-xs font-bold text-emerald-800 dark:text-emerald-300">
                                PROMESA DE PAGO
                                {gestion.MontoPrometido ? ` · USD ${Number(gestion.MontoPrometido).toFixed(2)}` : ''}
                                {gestion.FechaPrometida ? ` · ${formatFecha(gestion.FechaPrometida)}` : ''}
                            </p>
                            {gestion.EvidenciaPromesa && (
                                <p className="text-sm text-emerald-900 dark:text-emerald-200/90 italic mt-1">“{gestion.EvidenciaPromesa}”</p>
                            )}
                        </div>
                    )}

                    <Dato label="Resumen">{gestion.Resumen || gestion.Notas}</Dato>
                    {gestion.MotivoNoPago && <Dato label="Motivo de no pago">{gestion.MotivoNoPago}</Dato>}
                    {gestion.AlertaSupervisor && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800 dark:bg-rose-950/40 dark:border-rose-800/50 dark:text-rose-200/90">
                            <span className="font-bold">Alerta para supervisor: </span>{gestion.AlertaSupervisor}
                        </div>
                    )}

                    {turnos.length > 0 && (
                        <div>
                            <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Conversación</p>
                            <div className="space-y-2 p-3 bg-slate-50 rounded-xl border border-slate-200 dark:bg-[#0D131F] dark:border-slate-800">
                                {turnos.map((t, i) => (
                                    <div key={i} className={`flex ${t.quien === 'Cliente' ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${t.quien === 'Cliente'
                                            ? 'bg-blue-600 dark:bg-indigo-600 text-white rounded-br-sm'
                                            : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm dark:bg-[#161F30] dark:border-slate-700 dark:text-slate-200'}`}>
                                            <p className={`text-[10px] font-bold mb-0.5 ${t.quien === 'Cliente' ? 'text-blue-100' : 'text-slate-500 dark:text-slate-400'}`}>{t.quien}</p>
                                            {t.texto}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
